import { z } from 'zod';
import { TICKER_SYMBOL_RE } from '../context';

/**
 * The shared shape of one board-catalog entry, plus the schema helpers entries reuse.
 *
 * ZOD-3 COMPATIBILITY (same rule as widgets.ts/boards.ts): apps/web bundles this SOURCE
 * with zod v3 while the backend runs v4 — only syntax valid in BOTH may be used here.
 */

export const SymbolSchema = z.string().regex(TICKER_SYMBOL_RE);

/**
 * Zod 4's own uuid pattern (RFC 9562 versions 1-8 plus the nil and max ids), spelled out
 * because no uuid call is valid AND undeprecated in both zods: v4 deprecates
 * `z.string().uuid()` (Sonar S1874) and v3 has no top-level `z.uuid()` (web's bundle breaks).
 * Keeping v4's exact pattern means the backend accepts precisely what it did before.
 */
const UUID_RE =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/;

/**
 * A user-owned resource id (portfolio, watchlist) — the same validator context.ts uses. A
 * non-uuid never reaches the panel's fetch; a foreign or stale uuid is the server's 404
 * (every owned read filters by the caller), rendered as the panel's "not available" state.
 */
export const OwnedIdSchema = z.string().regex(UUID_RE);

/** An ISO 4217 display currency (context.ts's `displayCurrency` shape, upper-cased). */
export const CurrencySchema = z.string().regex(/^[A-Z]{3}$/);

/** The panel tags — the gallery/board chrome groups by these. */
export type CatalogCategory =
  | 'valuation'
  | 'data'
  | 'quality'
  | 'portfolio'
  | 'news'
  | 'market'
  | 'watchlist';

export interface CatalogEntry<P extends z.ZodTypeAny = z.ZodTypeAny> {
  /** MUST equal the record key (pinned by the FE parity spec, not prose). */
  id: string;
  category: CatalogCategory;
  /** Composer-facing "use when" — tool-description discipline. */
  description: string;
  /** The props schema — the ONLY thing a catalog block may carry besides its panel id. */
  props: P;
  /** The `set-param` surface (§4.3): the ONLY keys a set-param action may touch. */
  adjustable: readonly (keyof z.infer<P> & string)[];
  /** Rendered over the panel ("Research › Projections › ..."). */
  breadcrumb: readonly string[];
  /** Few-shot for the composer AND the FE smoke-test's mount input (must parse). */
  example: z.infer<P>;
  /** Excluded from the composer prompt; still renderable (historical boards). */
  retired?: boolean;
}

/** Identity helper that pins per-entry inference (example/adjustable typed against props). */
export function defineCatalogEntry<P extends z.ZodTypeAny>(
  entry: CatalogEntry<P>,
): CatalogEntry<P> {
  return entry;
}
